import set from 'lodash.set'
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range'

const getOrderBy = (sortModel, initialSortModel) => {
  const field = sortModel?.[0]?.field
  const sort = sortModel?.[0]?.sort

  if (field && sort) {
    const orderBy = {}
    set(orderBy, field, sort)
    return orderBy
  } else {
    const orderBy = {}
    const initialField = initialSortModel?.[0].field
    const initialSort = initialSortModel?.[0].sort
    set(orderBy, initialField, initialSort)
    return orderBy
  }
}

const getIsNumber = string => {
  if (typeof string !== 'string') {
    return false
  }

  return !isNaN(string) && !isNaN(parseFloat(string))
}

const formatJsonFilterValue = value => {
  const trimValue = value.trim()
  const upperValue = value.trim().toUpperCase()
  const isNumber = getIsNumber(trimValue)

  if (isNumber) {
    return Number(trimValue)
  }

  if (upperValue === 'TRUE') {
    return true
  }

  if (upperValue === 'FALSE') {
    return false
  }

  return trimValue
}

const getHasuraValue = (value, muiOperator, isCustomField) => {
  switch (muiOperator) {
    case 'contains': {
      if (isCustomField) {
        const words = value?.split(':')
        const key = words?.[0] || ''
        const keyValue = words?.[1] || ''
        const formattedValue = formatJsonFilterValue(keyValue)

        if (key && formattedValue !== undefined) {
          const result = {
            [key]: formattedValue
          }

          return result
        } else {
          return {}
        }
      } else {
        return `%${value}%`
      }
    }

    case 'startsWith':
      return `${value}%`

    case 'endsWith':
      return `%${value}`

    case 'before':
    case 'onOrAfter':
      return moment(value).startOf('day')

    case 'after':
    case 'onOrBefore':
      return moment(value).endOf('day')

    case '=':
    case '!=':
    case '>':
    case '>=':
    case '<':
    case '<=':
      return parseInt(value)

    case 'isEmpty':
      return true

    case 'isNotEmpty':
      return false

    default:
      return value
  }
}

const getHasuraOperator = (muiOperator, isCustomField = false) => {
  switch (muiOperator) {
    case 'and':
      return '_and'

    case 'or':
      return '_or'

    case 'equals':
    case 'is':
    case '=':
      return '_eq'

    case '!=':
      return '_neq'

    case '>':
    case 'after':
      return '_gt'

    case '<':
    case 'before':
      return '_lt'

    case '>=':
    case 'onOrAfter':
      return '_gte'

    case '<=':
    case 'onOrBefore':
      return '_lte'

    case 'contains': {
      if (isCustomField) {
        return '_contains'
      } else {
        return '_ilike'
      }
    }

    case 'startsWith':
    case 'endsWith':
      return '_ilike'

    case 'isEmpty':
    case 'isNotEmpty':
      return '_is_null'

    default:
      throw new Error(`operator ${muiOperator} not available`)
  }
}

const getWhere = filterModel => {
  if (!filterModel) {
    return {}
  }

  const linkOperator = getHasuraOperator(filterModel.linkOperator)
  const where = { [linkOperator]: [] }
  for (const item of filterModel.items) {
    const { columnField, operatorValue, value } = item
    const isCustomField = columnField === 'custom_fields'

    if (value || ['isEmpty', 'isNotEmpty'].includes(operatorValue)) {
      const hasuraOperator = getHasuraOperator(operatorValue, isCustomField)
      const hasuraValue = getHasuraValue(value, operatorValue, isCustomField)
      const whereItem = {}
      set(whereItem, `${columnField}.${hasuraOperator}`, hasuraValue)
      where[linkOperator].push(whereItem)
    }
  }

  return where
}

const getLocales = t => {
  return {
    noRowsLabel: t('noRowsLabel'),
    errorOverlayDefaultLabel: t('errorOverlayDefaultLabel'),
    toolbarDensity: t('toolbarDensity'),
    toolbarDensityLabel: t('toolbarDensityLabel'),
    toolbarDensityCompact: t('toolbarDensityCompact'),
    toolbarDensityStandard: t('toolbarDensityStandard'),
    toolbarDensityComfortable: t('toolbarDensityComfortable'),
    toolbarColumns: t('toolbarColumns'),
    toolbarColumnsLabel: t('toolbarColumnsLabel'),
    toolbarFilters: t('toolbarFilters'),
    toolbarFiltersLabel: t('toolbarFiltersLabel'),
    toolbarFiltersTooltipHide: t('toolbarFiltersTooltipHide'),
    toolbarFiltersTooltipShow: t('toolbarFiltersTooltipShow'),
    toolbarExport: t('toolbarExport'),
    toolbarExportLabel: t('toolbarExportLabel'),
    toolbarExportCSV: t('toolbarExportCSV'),
    columnsPanelTextFieldLabel: t('columnsPanelTextFieldLabel'),
    columnsPanelTextFieldPlaceholder: t('columnsPanelTextFieldPlaceholder'),
    columnsPanelDragIconLabel: t('columnsPanelDragIconLabel'),
    columnsPanelShowAllButton: t('columnsPanelShowAllButton'),
    columnsPanelHideAllButton: t('columnsPanelHideAllButton'),
    filterPanelAddFilter: t('filterPanelAddFilter'),
    filterPanelDeleteIconLabel: t('filterPanelDeleteIconLabel'),
    filterPanelOperators: t('filterPanelOperators'),
    filterPanelOperatorAnd: t('filterPanelOperatorAnd'),
    filterPanelOperatorOr: t('filterPanelOperatorOr'),
    filterPanelColumns: t('filterPanelColumns'),
    filterPanelInputLabel: t('filterPanelInputLabel'),
    filterPanelInputPlaceholder: t('filterPanelInputPlaceholder'),
    filterOperatorContains: t('filterOperatorContains'),
    filterOperatorEquals: t('filterOperatorEquals'),
    filterOperatorStartsWith: t('filterOperatorStartsWith'),
    filterOperatorEndsWith: t('filterOperatorEndsWith'),
    filterOperatorIs: t('filterOperatorIs'),
    filterOperatorNot: t('filterOperatorNot'),
    filterOperatorAfter: t('filterOperatorAfter'),
    filterOperatorOnOrAfter: t('filterOperatorOnOrAfter'),
    filterOperatorBefore: t('filterOperatorBefore'),
    filterOperatorOnOrBefore: t('filterOperatorOnOrBefore'),
    filterOperatorIsEmpty: t('filterOperatorIsEmpty'),
    filterOperatorIsNotEmpty: t('filterOperatorIsNotEmpty'),
    columnMenuLabel: t('columnMenuLabel'),
    columnMenuShowColumns: t('columnMenuShowColumns'),
    columnMenuFilter: t('columnMenuFilter'),
    columnMenuHideColumn: t('columnMenuHideColumn'),
    columnMenuUnsort: t('columnMenuUnsort'),
    columnMenuSortAsc: t('columnMenuSortAsc'),
    columnMenuSortDesc: t('columnMenuSortDesc'),
    columnHeaderFiltersLabel: t('columnHeaderFiltersLabel'),
    columnHeaderSortIconLabel: t('columnHeaderSortIconLabel'),
    footerTotalRows: t('footerTotalRows'),
    pinToLeft: t('pinToLeft'),
    pinToRight: t('pinToRight'),
    unpin: t('unpin'),
    columnHeaderFiltersTooltipActive: count =>
      count > 1
        ? `${count} ${t('activeFilters')}`
        : `${count} ${t('activeFilter')}`,
    footerRowSelected: count =>
      count > 1
        ? `${count.toLocaleString()} ${t('rowsSelected')}`
        : `${count.toLocaleString()} ${t('rowSelected')}`,
    footerTotalVisibleRows: (visibleCount, totalCount) =>
      `${visibleCount.toLocaleString()} ${t(
        'of'
      )} ${totalCount.toLocaleString()}`,
    toolbarFiltersTooltipActive: count =>
      count > 1
        ? `${count} ${t('activeFilters')}`
        : `${count} ${t('activeFilter')}`
  }
}

const generatePath = ({
  pageSize = 10,
  page = 0,
  sortModel = [{ field: 'id', sort: 'desc' }],
  filterModel
}) =>
  `?pageSize=${pageSize}&page=${page}&sortModel=${JSON.stringify(
    sortModel
  )}&filterModel=${JSON.stringify(filterModel)}`.replace('+', '%2B')

export default {
  getOrderBy,
  getHasuraValue,
  getHasuraOperator,
  getWhere,
  getLocales,
  generatePath
}
